/**
 * logScale — logarithmic slider mapping.
 *
 * ENG-895: the environment scale slider used to be linear 0.01..10. Raising the
 * ceiling to 1000 linearly would give ~0.01 resolution at the low end, where
 * most environments actually sit — trading one usability bug for another. A log
 * mapping keeps a constant *ratio* per pixel instead of a constant difference,
 * so 0.5 → 0.6 and 50 → 60 take the same drag distance.
 *
 * Pure math, no DOM. Slider position is always normalised 0..1.
 */

/** Value → normalised slider position (0..1). Clamped to [min, max]. */
export function toSliderPos(value, min, max) {
  if (!(min > 0) || !(max > min)) return 0
  const v = Number(value)
  if (!Number.isFinite(v) || v <= 0) return 0
  const clamped = Math.min(Math.max(v, min), max)
  return (Math.log(clamped) - Math.log(min)) / (Math.log(max) - Math.log(min))
}

/** Normalised slider position (0..1) → value. Clamped to [min, max]. */
export function fromSliderPos(pos, min, max) {
  if (!(min > 0) || !(max > min)) return min
  const t = Number(pos)
  if (!Number.isFinite(t)) return min
  const clamped = Math.min(Math.max(t, 0), 1)
  // exp(log(min) + 1*(log(max)-log(min))) lands on 999.9999999999989, not 1000.
  // Dragging to the end of the slider must produce the stated bound exactly —
  // otherwise the value saved into default_pose is visibly off-by-epsilon.
  if (clamped === 0) return min
  if (clamped === 1) return max
  return Math.exp(Math.log(min) + clamped * (Math.log(max) - Math.log(min)))
}

/**
 * Round to a sensible number of decimals for display/commit, scaled to the
 * magnitude: 0.01 steps below 1, 0.1 below 100, whole numbers above. Without
 * this a log slider emits values like 47.38295745 which read as noise in the
 * numeric field and in the saved default_pose.
 */
export function quantize(value) {
  const v = Number(value)
  if (!Number.isFinite(v)) return v
  const a = Math.abs(v)
  if (a < 1) return Math.round(v * 1000) / 1000
  if (a < 100) return Math.round(v * 100) / 100
  return Math.round(v * 10) / 10
}
